import { useEffect, useState } from 'react';
import { Plus, CreditCard as Edit, Trash2, Save, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from './ConfirmModal';
import { useNotify } from './useNotify';
import { formatTimeOfDay } from '../lib/timeFormat';
import { readStartTimes } from '../lib/waterRestrictions';

export interface WaterProgram {
  program: 'A' | 'B' | 'C' | 'D';
  programName: string;
  /** A program can run at several start times (all share these days/duration). */
  startTimes: string[];
  duration: number;
  days: string[];
}

export type WaterSchedule = {
  programs: WaterProgram[];
};

interface WaterScheduleManagerProps {
  schedule: WaterSchedule;
  onChange: (schedule: WaterSchedule) => void;
}

const programLetters: ('A' | 'B' | 'C' | 'D')[] = ['A', 'B', 'C', 'D'];

const programColors: Record<string, string> = {
  A: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/30',
  B: 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/30',
  C: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/30',
  D: 'bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-500/20 dark:text-rose-300 dark:border-rose-500/30',
};

export function WaterScheduleManager({ schedule, onChange }: WaterScheduleManagerProps) {
  const { t, i18n } = useTranslation('inspection');
  const { t: tCommon } = useTranslation('common');
  const { notify, NotifyModal } = useNotify();
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    program: 'A' as 'A' | 'B' | 'C' | 'D',
    programName: '',
    startTimes: ['06:00'] as string[],
    duration: 15,
    days: [] as string[],
  });

  const daysOfWeek = [
    { key: 'Sun', label: t('schedule.dayShortSun'), longLabel: t('schedule.daySun') },
    { key: 'Mon', label: t('schedule.dayShortMon'), longLabel: t('schedule.dayMon') },
    { key: 'Tue', label: t('schedule.dayShortTue'), longLabel: t('schedule.dayTue') },
    { key: 'Wed', label: t('schedule.dayShortWed'), longLabel: t('schedule.dayWed') },
    { key: 'Thu', label: t('schedule.dayShortThu'), longLabel: t('schedule.dayThu') },
    { key: 'Fri', label: t('schedule.dayShortFri'), longLabel: t('schedule.dayFri') },
    { key: 'Sat', label: t('schedule.dayShortSat'), longLabel: t('schedule.daySat') },
  ];

  const getDefaultProgramName = (prog: 'A' | 'B' | 'C' | 'D'): string => {
    switch (prog) {
      case 'A': return t('schedule.programDefaultA');
      case 'B': return t('schedule.programDefaultB');
      case 'C': return t('schedule.programDefaultC');
      case 'D': return t('schedule.programDefaultD');
    }
  };

  const programs = schedule.programs || [];

  // If the schedule's programs change underneath an open edit form (e.g. the
  // controller's schedule is reloaded/replaced by the parent), a previously-set
  // editingIndex can point past the end of the new array. Exit edit mode so we
  // don't keep rendering a form for a program that no longer exists.
  useEffect(() => {
    if (editingIndex !== null && editingIndex >= programs.length) {
      setEditingIndex(null);
      setIsAdding(false);
    }
  }, [editingIndex, programs.length]);

  const getUsedPrograms = (): Set<string> => {
    return new Set(programs.map((p) => p.program));
  };

  const getAvailablePrograms = (): ('A' | 'B' | 'C' | 'D')[] => {
    const used = getUsedPrograms();
    // Guard against a stale editingIndex: the reset effect above runs only after
    // this render, so a dangling index must not crash the render in between
    // (feedback be0011bb: "undefined is not an object (evaluating '…program')").
    const editing = editingIndex !== null ? programs[editingIndex] : undefined;
    if (editing) used.delete(editing.program);
    return programLetters.filter((p) => !used.has(p));
  };

  const handleAdd = () => {
    const available = getAvailablePrograms();
    if (available.length === 0) {
      notify(tCommon('status.noticeTitle'), t('schedule.errorAllConfigured'), 'warning');
      return;
    }
    const nextProgram = available[0];
    setIsAdding(true);
    setEditingIndex(null);
    setFormData({
      program: nextProgram,
      programName: getDefaultProgramName(nextProgram),
      startTimes: ['06:00'],
      duration: 15,
      days: [],
    });
  };

  const handleEdit = (index: number) => {
    const prog = programs[index];
    setEditingIndex(index);
    setIsAdding(false);
    // Tolerate legacy single-startTime records via readStartTimes; never empty.
    const times = readStartTimes(prog);
    setFormData({
      program: prog.program,
      programName: prog.programName || getDefaultProgramName(prog.program),
      startTimes: times.length > 0 ? times : ['06:00'],
      duration: prog.duration,
      days: [...prog.days],
    });
  };

  const handleSave = () => {
    if (formData.days.length === 0) {
      notify(tCommon('status.noticeTitle'), t('schedule.errorDayRequired'), 'warning');
      return;
    }

    const cleanTimes = formData.startTimes.map((t) => t.trim()).filter((t) => !!t);
    if (cleanTimes.length === 0) {
      notify(tCommon('status.noticeTitle'), t('schedule.errorStartTimeRequired'), 'warning');
      return;
    }

    const newProgram: WaterProgram = {
      program: formData.program,
      programName: formData.programName.trim() || getDefaultProgramName(formData.program),
      startTimes: cleanTimes,
      duration: formData.duration,
      days: formData.days,
    };

    if (editingIndex !== null) {
      const updated = [...programs];
      updated[editingIndex] = newProgram;
      onChange({ programs: updated });
      setEditingIndex(null);
    } else {
      onChange({ programs: [...programs, newProgram] });
      setIsAdding(false);
    }

    setFormData({
      program: 'A',
      programName: '',
      startTimes: ['06:00'],
      duration: 15,
      days: [],
    });
  };

  const updateStartTime = (idx: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      startTimes: prev.startTimes.map((t, i) => (i === idx ? value : t)),
    }));
  };
  const addStartTime = () => {
    setFormData((prev) => ({ ...prev, startTimes: [...prev.startTimes, '06:00'] }));
  };
  const removeStartTime = (idx: number) => {
    setFormData((prev) => ({
      ...prev,
      startTimes: prev.startTimes.length > 1 ? prev.startTimes.filter((_, i) => i !== idx) : prev.startTimes,
    }));
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingIndex(null);
  };

  const handleDelete = (index: number) => {
    setDeleteConfirmIndex(index);
  };

  const confirmDelete = () => {
    if (deleteConfirmIndex === null) return;
    const updated = programs.filter((_, i) => i !== deleteConfirmIndex);
    onChange({ programs: updated });
    setDeleteConfirmIndex(null);
  };

  const toggleDay = (dayKey: string) => {
    setFormData((prev) => ({
      ...prev,
      days: prev.days.includes(dayKey)
        ? prev.days.filter((d) => d !== dayKey)
        : [...prev.days, dayKey],
    }));
  };

  const selectAllDays = () => {
    setFormData((prev) => ({
      ...prev,
      days: daysOfWeek.map((d) => d.key),
    }));
  };

  const clearAllDays = () => {
    setFormData((prev) => ({
      ...prev,
      days: [],
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-theme-primary">{t('schedule.heading')}</h3>
          <p className="text-sm text-theme-secondary">{t('schedule.subheading')}</p>
        </div>
        {!isAdding && editingIndex === null && (
          <button
            type="button"
            onClick={handleAdd}
            disabled={getAvailablePrograms().length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={18} />
            {t('schedule.addProgram')}
          </button>
        )}
      </div>

      {(isAdding || editingIndex !== null) && (
        <div className="bg-subtle border border-line rounded-lg p-5">
          <h4 className="text-md font-semibold text-theme-primary mb-4">
            {editingIndex !== null ? t('schedule.editTitle') : t('schedule.addTitle')}
          </h4>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1">
                  {t('schedule.programLabel')}
                </label>
                <select
                  value={formData.program}
                  onChange={(e) => {
                    const prog = e.target.value as 'A' | 'B' | 'C' | 'D';
                    setFormData({
                      ...formData,
                      program: prog,
                      programName: formData.programName || getDefaultProgramName(prog),
                    });
                  }}
                  className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {getAvailablePrograms().map((prog) => (
                    <option key={prog} value={prog}>
                      {t('schedule.programLetter', { letter: prog })}
                    </option>
                  ))}
                  {editingIndex !== null && (
                    <option value={formData.program}>
                      {t('schedule.programLetter', { letter: formData.program })}
                    </option>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1">
                  {t('schedule.programNameRequired')}
                </label>
                <input
                  type="text"
                  value={formData.programName}
                  onChange={(e) => setFormData({ ...formData, programName: e.target.value })}
                  placeholder={t('schedule.programNamePlaceholder', { default: getDefaultProgramName(formData.program) })}
                  className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-theme-primary mb-2">
                {t('schedule.daysOfWeek')}
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                {daysOfWeek.map((day, idx) => (
                  <button
                    key={`${day.key}-${idx}`}
                    type="button"
                    onClick={() => toggleDay(day.key)}
                    title={day.longLabel}
                    className={`w-10 h-10 rounded-full text-sm font-semibold transition-all duration-150 border-2 ${
                      formData.days.includes(day.key)
                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                        : 'bg-surface text-theme-secondary border-line hover:border-blue-400 hover:text-blue-600'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
                <div className="ml-2 flex gap-1">
                  <button
                    type="button"
                    onClick={selectAllDays}
                    className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded transition-colors"
                  >
                    {t('schedule.allDays')}
                  </button>
                  <button
                    type="button"
                    onClick={clearAllDays}
                    className="px-2 py-1 text-xs text-blue-600 hover:bg-subtle rounded transition-colors"
                  >
                    {t('schedule.noneDays')}
                  </button>
                </div>
              </div>
              <p className="text-xs text-theme-secondary mt-1">
                {formData.days.length > 0
                  ? t('schedule.selectedDays', { days: formData.days.join(', ') })
                  : t('schedule.noDaysSelected')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1">
                  {t('schedule.startTimes')}
                </label>
                <div className="space-y-2">
                  {formData.startTimes.map((time, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="time"
                        value={time}
                        onChange={(e) => updateStartTime(idx, e.target.value)}
                        className="flex-1 px-3 py-2 border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      {formData.startTimes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeStartTime(idx)}
                          title={t('schedule.removeStartTime')}
                          className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addStartTime}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-theme-primary font-medium"
                  >
                    <Plus size={14} /> {t('schedule.addStartTime')}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1">
                  {t('schedule.duration')}
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={handleSave}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Save size={18} />
              {tCommon('actions.save')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center gap-2 px-4 py-2 bg-subtle text-theme-primary rounded-lg hover:bg-subtle transition-colors"
            >
              <X size={18} />
              {tCommon('actions.cancel')}
            </button>
          </div>
        </div>
      )}

      {programs.length === 0 ? (
        <div className="text-center py-8 border border-line rounded-lg bg-subtle">
          <p className="text-theme-secondary">{t('schedule.empty')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {programs.map((prog, index) => (
            <div
              key={`${prog.program}-${index}`}
              className={`border rounded-lg p-4 ${programColors[prog.program].replace('text-', 'border-').split(' ')[2] || 'border-line'} bg-surface`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg font-bold text-sm border ${programColors[prog.program]}`}>
                    {prog.program}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-theme-primary">
                        {t('schedule.programLetter', { letter: prog.program })}
                      </h4>
                      <span className="text-sm text-theme-secondary">-</span>
                      <span className="text-sm font-medium text-theme-primary">
                        {prog.programName || getDefaultProgramName(prog.program)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-theme-secondary">
                      <span>
                        {t('schedule.daysList', { days: prog.days.length > 0 ? prog.days.join(', ') : t('schedule.daysNone') })}
                      </span>
                      <span>{t('schedule.startTimeShort', { time: readStartTimes(prog).map((st) => formatTimeOfDay(st, i18n.language)).join(', ') })}</span>
                      <span>{t('schedule.durationShort', { n: prog.duration })}</span>
                    </div>
                    <div className="flex gap-1 mt-2">
                      {daysOfWeek.map((day, idx) => (
                        <span
                          key={`${day.key}-${idx}`}
                          className={`w-7 h-7 rounded-full text-xs flex items-center justify-center font-medium ${
                            prog.days.includes(day.key)
                              ? 'bg-blue-600 text-white'
                              : 'bg-subtle text-theme-secondary'
                          }`}
                        >
                          {day.label}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 ml-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(index)}
                    className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
                    title={tCommon('actions.edit')}
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(index)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                    title={tCommon('actions.delete')}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirmIndex !== null}
        title={t('schedule.confirmDeleteTitle')}
        message={t('schedule.confirmDeleteMessage')}
        confirmText={tCommon('actions.delete')}
        cancelText={tCommon('actions.cancel')}
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirmIndex(null)}
      />

      {NotifyModal}
    </div>
  );
}
